import {
  type ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  DashboardLayoutConfig,
  GlobalFilters,
  GridLayout,
  WidgetSettings,
  BreakpointLayouts,
} from "../../../models/Dashboard";
import { widgetRegistry } from "../../../services/dashboard/WidgetRegistryService";
import { DashboardApi } from "../../../services/api/DashboardApi";
import { widgetDefinitions } from "./definitions";
import { placeWidgetInLayouts } from "./gridPlacement";

const DEFAULT_WIDGET_LAYOUTS: BreakpointLayouts = {
  lg: [
    { i: "activations-loads-1", x: 0, y: 0, w: 4, h: 10, minW: 4, minH: 8 },
    { i: "mcc-top-n-1", x: 4, y: 0, w: 4, h: 20, minW: 4, minH: 8 },
    { i: "merchant-top-n-1", x: 8, y: 0, w: 4, h: 20, minW: 4, minH: 8 },
    { i: "settlement-volume-1", x: 0, y: 10, w: 4, h: 10, minW: 4, minH: 8 },
  ],
  md: [
    { i: "activations-loads-1", x: 0, y: 0, w: 5, h: 10, minW: 4, minH: 8 },
    { i: "mcc-top-n-1", x: 5, y: 0, w: 5, h: 10, minW: 4, minH: 8 },
    { i: "merchant-top-n-1", x: 0, y: 10, w: 5, h: 10, minW: 4, minH: 8 },
    { i: "settlement-volume-1", x: 5, y: 10, w: 5, h: 10, minW: 4, minH: 8 },
  ],
  sm: [
    { i: "activations-loads-1", x: 0, y: 0, w: 6, h: 9, minW: 4, minH: 8 },
    { i: "mcc-top-n-1", x: 0, y: 9, w: 6, h: 9, minW: 4, minH: 8 },
    { i: "merchant-top-n-1", x: 0, y: 18, w: 6, h: 9, minW: 4, minH: 8 },
    { i: "settlement-volume-1", x: 0, y: 27, w: 6, h: 9, minW: 4, minH: 8 },
  ],
  xs: [
    { i: "activations-loads-1", x: 0, y: 0, w: 4, h: 9, minW: 4, minH: 8 },
    { i: "mcc-top-n-1", x: 0, y: 9, w: 4, h: 9, minW: 4, minH: 8 },
    { i: "merchant-top-n-1", x: 0, y: 18, w: 4, h: 9, minW: 4, minH: 8 },
    { i: "settlement-volume-1", x: 0, y: 27, w: 4, h: 9, minW: 4, minH: 8 },
  ],
  xxs: [
    { i: "activations-loads-1", x: 0, y: 0, w: 2, h: 9, minW: 2, minH: 8 },
    { i: "mcc-top-n-1", x: 0, y: 9, w: 2, h: 9, minW: 2, minH: 8 },
    { i: "merchant-top-n-1", x: 0, y: 18, w: 2, h: 9, minW: 2, minH: 8 },
    { i: "settlement-volume-1", x: 0, y: 27, w: 2, h: 9, minW: 2, minH: 8 },
  ],
};

const cloneLayouts = (layouts: BreakpointLayouts): BreakpointLayouts => {
  const clone = (items?: GridLayout[]) => items?.map((item) => ({ ...item }));
  return {
    lg: clone(layouts.lg),
    md: clone(layouts.md),
    sm: clone(layouts.sm),
    xs: clone(layouts.xs),
    xxs: clone(layouts.xxs),
  };
};

const getDefaultWidgetSettings = (definitionId: string): WidgetSettings => {
  const definition = widgetDefinitions.find((d) => d.id === definitionId);
  return { ...(definition?.defaultSettings ?? {}) };
};

const buildSeedLayout = (
  layoutKey: string,
  existing?: DashboardLayoutConfig | null
): DashboardLayoutConfig => ({
  layoutId: existing?.layoutId,
  layoutKey: existing?.layoutKey ?? layoutKey,
  layoutName: existing?.layoutName || "Default Dashboard",
  isDefault: existing?.isDefault ?? layoutKey === "default",
  rowHeight: existing?.rowHeight ?? 28,
  cols: existing?.cols,
  layouts: cloneLayouts(DEFAULT_WIDGET_LAYOUTS),
  widgets: [
    {
      widgetId: "activations-loads-1",
      definitionId: "activations-loads",
      settings: getDefaultWidgetSettings("activations-loads"),
      isVisible: true,
    },
    {
      widgetId: "mcc-top-n-1",
      definitionId: "mcc-top-n",
      settings: getDefaultWidgetSettings("mcc-top-n"),
      isVisible: true,
    },
    {
      widgetId: "merchant-top-n-1",
      definitionId: "merchant-top-n",
      settings: getDefaultWidgetSettings("merchant-top-n"),
      isVisible: true,
    },
    {
      widgetId: "settlement-volume-1",
      definitionId: "settlement-volume",
      settings: getDefaultWidgetSettings("settlement-volume"),
      isVisible: true,
    },
  ],
  globalFilters: existing?.globalFilters ?? {},
});

const hasWidgets = (layout: DashboardLayoutConfig | null): boolean =>
  (layout?.widgets?.length ?? 0) > 0;

const hasGrid = (layout: DashboardLayoutConfig | null): boolean =>
  (layout?.layouts?.lg?.length ?? 0) > 0;

const hasVisibleWidgets = (layout: DashboardLayoutConfig | null): boolean =>
    (layout?.widgets?.filter((w) => w.isVisible !== false).length ?? 0) > 0;

const isLayoutEmpty = (layout: DashboardLayoutConfig | null): boolean => {
  if (!layout) return true;
  return !hasWidgets(layout);
};

const buildLayoutsForWidgets = (widgetIds: string[]): BreakpointLayouts => {
  const createForBreakpoint = (
    items: GridLayout[] | undefined,
    fallbackW: number,
    fallbackH: number,
    fallbackMinW: number
  ): GridLayout[] => {
    const usedY: number[] = [];
    return widgetIds.map((widgetId, index) => {
      const template = items?.find((item) => item.i === widgetId);
      if (template) {
        usedY.push(template.y + template.h);
        return { ...template };
      }

      const y = usedY.length > 0 ? Math.max(...usedY) : index * fallbackH;
      const generated: GridLayout = {
        i: widgetId,
        x: 0,
        y,
        w: fallbackW,
        h: fallbackH,
        minW: fallbackMinW,
        minH: 8,
      };
      usedY.push(generated.y + generated.h);
      return generated;
    });
  };

  return {
    lg: createForBreakpoint(DEFAULT_WIDGET_LAYOUTS.lg, 4, 10, 4),
    md: createForBreakpoint(DEFAULT_WIDGET_LAYOUTS.md, 5, 10, 4),
    sm: createForBreakpoint(DEFAULT_WIDGET_LAYOUTS.sm, 6, 9, 4),
    xs: createForBreakpoint(DEFAULT_WIDGET_LAYOUTS.xs, 4, 9, 4),
    xxs: createForBreakpoint(DEFAULT_WIDGET_LAYOUTS.xxs, 2, 9, 2),
  };
};

const repairMissingGrid = (
  layout: DashboardLayoutConfig
): DashboardLayoutConfig => ({
    ...layout,
    layouts: buildLayoutsForWidgets(
        layout.widgets.filter((w) => w.isVisible !== false).map((w) => w.widgetId)
    ),
});

//When a new widget is added to the seed, it would not be visable for existing users.
const reconcileNewWidgets = (
    layout: DashboardLayoutConfig
): DashboardLayoutConfig => {
    const seed = buildSeedLayout(layout.layoutKey ?? "default", layout);

    const knownDefinitionIds = new Set(
        layout.widgets.map((w) => w.definitionId)
    );

    const missing = seed.widgets.filter(
        (sw) => !knownDefinitionIds.has(sw.definitionId)
    );

    if (missing.length === 0) {
        return layout; 
    }

    // Append the missing widget instances (visible) and give each a grid slot.
    let mergedLayouts = layout.layouts;
    const mergedWidgets = [...layout.widgets];

    for (const seedWidget of missing) {
        mergedWidgets.push({
            widgetId: seedWidget.widgetId,
            definitionId: seedWidget.definitionId,
            isVisible: true,
            settings: seedWidget.settings,
        });
        mergedLayouts = placeWidgetInLayouts(mergedLayouts, seedWidget.widgetId);
    }

    return {
        ...layout,
        widgets: mergedWidgets,
        layouts: mergedLayouts,
    };
};


interface WidgetContextValue {
  layouts: DashboardLayoutConfig | null;
  globalFilters: GlobalFilters;
  isLoading: boolean;

  updateWidgetSettings(widgetId: string, newSettings: WidgetSettings): Promise<void>;
  saveWidgetSettings(widgetId: string, settings?: WidgetSettings): Promise<void>;
  deleteWidget(widgetId: string): Promise<void>;
  addWidget(definitionId: string): Promise<void>;
  hideWidget(widgetId: string): Promise<void>;
  showWidget(widgetId: string): Promise<void>;
  addOrShowWidget(definitionId: string): Promise<void>;

  updateGlobalFilters(filters: GlobalFilters): Promise<void>;

  refreshWidget(widgetId: string): void;
  refreshAll(): void;
  updateLayout(nextLayouts: BreakpointLayouts): Promise<void>;

  saveLayout(): Promise<void>;
  loadLayout(layoutId: string): Promise<void>;
}

const WidgetContext = createContext<WidgetContextValue | undefined>(undefined);

export const WidgetProvider = ({
  children,
  layoutId = "default",
}: {
  children: ReactNode;
  layoutId?: string;
}) => {
  const [layouts, setLayouts] = useState<DashboardLayoutConfig | null>(null);
  const [globalFilters, setGlobalFilters] = useState<GlobalFilters>({});
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTokens, setRefreshTokens] = useState<Record<string, number>>({});

  useEffect(() => {
    const loadInitialLayout = async () => {
      try {
        setIsLoading(true);
        const layout = await DashboardApi.loadLayoutConfig(layoutId);

        if (isLayoutEmpty(layout)) {
          const seededLayout = buildSeedLayout(layoutId, layout);
          await DashboardApi.saveLayoutConfig(seededLayout);
          setLayouts(seededLayout);
          setGlobalFilters(seededLayout.globalFilters || {});
          return;
        }

          if (layout && hasVisibleWidgets(layout) && !hasGrid(layout)) {
          const repairedLayout = repairMissingGrid(layout);
          await DashboardApi.saveLayoutConfig(repairedLayout);
          setLayouts(repairedLayout);
          setGlobalFilters(repairedLayout.globalFilters || {});
          return;
        }

        if (!layout) return;

        const reconciled = reconcileNewWidgets(layout);
        if (reconciled !== layout) {
            await DashboardApi.saveLayoutConfig(reconciled);
        }

        setLayouts(reconciled);
        setGlobalFilters(reconciled.globalFilters || {});
      } catch (error) {
        console.error("Failed to load layout:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialLayout();
  }, [layoutId]);

  const updateWidgetSettings = useCallback(
    async (widgetId: string, newSettings: WidgetSettings) => {
      setLayouts((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          widgets: prev.widgets.map((w) =>
            w.widgetId === widgetId
              ? { ...w, settings: newSettings }
              : w
          ),
        };
      });
    },
    []
  );

  const saveWidgetSettings = useCallback(
    async (widgetId: string, settings?: WidgetSettings) => {
      if (!layouts) return;

      const widget = layouts.widgets.find((w) => w.widgetId === widgetId);
      if (!widget) return;

      const settingsToSave = settings ?? widget.settings;
      const updatedLayout: DashboardLayoutConfig = {
        ...layouts,
        widgets: layouts.widgets.map((w) =>
          w.widgetId === widgetId
            ? { ...w, settings: settingsToSave }
            : w
        ),
      };

      try {
        await DashboardApi.saveLayoutConfig(updatedLayout);
        setLayouts(updatedLayout);
      } catch (error) {
        console.error(
          `Failed to save settings for widget ${widgetId}:`,
          error
        );
        throw error;
      }
    },
    [layouts]
  );

  const deleteWidget = useCallback(async (widgetId: string) => {
    if (!layouts) return;

    try {
      await DashboardApi.deleteWidget(layouts.layoutKey, widgetId);

      setLayouts((prev) => {
        if (!prev) return prev;
        const filterBp = (items?: GridLayout[]) =>
          items?.filter((l) => l.i !== widgetId);
        return {
          ...prev,
          widgets: prev.widgets.filter((w) => w.widgetId !== widgetId),
          layouts: {
            lg: filterBp(prev.layouts.lg),
            md: filterBp(prev.layouts.md),
            sm: filterBp(prev.layouts.sm),
            xs: filterBp(prev.layouts.xs),
            xxs: filterBp(prev.layouts.xxs),
          },
        };
      });
    } catch (error) {
      console.error(`Failed to delete widget ${widgetId}:`, error);
      throw error;
    }
  }, [layouts]);

  const addWidget = useCallback(async (definitionId: string) => {
      if (!layouts) return;

      const definition = widgetRegistry.get(definitionId);
      if (!definition) {
          console.error(`Widget definition "${definitionId}" not found`);
          return;
      }

      const widgetId = `${definitionId}-${Date.now()}`;

      const newWidgets = [
          ...layouts.widgets,
          {
              widgetId,
              definitionId,
              isVisible: true,
              settings: definition.defaultSettings,
          },
      ];

      const newLayout: DashboardLayoutConfig = {
          ...layouts,
          widgets: newWidgets,
          layouts: placeWidgetInLayouts(layouts.layouts, widgetId, definition?.defaultSizes),
      };

      try {
          await DashboardApi.saveLayoutConfig(newLayout);
          setLayouts(newLayout);
      } catch (error) {
          console.error("Failed to add widget:", error);
          throw error;
      }
  }, [layouts]);


  const hideWidget = useCallback(async (widgetId: string) => {
      if (!layouts) return;

      const filterBp = (items?: GridLayout[]) =>
          items?.filter((item) => item.i !== widgetId);

      const newLayout: DashboardLayoutConfig = {
          ...layouts,
          widgets: layouts.widgets.map((w) =>
              w.widgetId === widgetId ? { ...w, isVisible: false } : w
          ),
          layouts: {
              lg: filterBp(layouts.layouts.lg),
              md: filterBp(layouts.layouts.md),
              sm: filterBp(layouts.layouts.sm),
              xs: filterBp(layouts.layouts.xs),
              xxs: filterBp(layouts.layouts.xxs),
          },
      };

      try {
          await DashboardApi.saveLayoutConfig(newLayout);
          setLayouts(newLayout);
      } catch (error) {
          console.error(`Failed to hide widget ${widgetId}:`, error);
          throw error;
      }
  }, [layouts]);

  const showWidget = useCallback(async (widgetId: string) => {
      if (!layouts) return;

      const widget = layouts.widgets.find((w) => w.widgetId === widgetId);
      const definition = widget ? widgetRegistry.get(widget.definitionId) : undefined;

      const newWidgets = layouts.widgets.map((w) =>
          w.widgetId === widgetId ? { ...w, isVisible: true } : w
      );

      const newLayout: DashboardLayoutConfig = {
          ...layouts,
          widgets: newWidgets,
          layouts: placeWidgetInLayouts(layouts.layouts, widgetId, definition?.defaultSizes),
      };

      try {
          await DashboardApi.saveLayoutConfig(newLayout);
          setLayouts(newLayout);
      } catch (error) {
          console.error(`Failed to show widget ${widgetId}:`, error);
          throw error;
      }
  }, [layouts]);

  const addOrShowWidget = useCallback(async (definitionId: string) => {
      if (!layouts) return;

      const definition = widgetRegistry.get(definitionId);
      if (!definition) {
          console.error(`Widget definition "${definitionId}" not found`);
          return;
      }

      const instances = layouts.widgets.filter((w) => w.definitionId === definitionId);
      const visible = instances.filter((w) => w.isVisible !== false);
      const hidden = instances.filter((w) => w.isVisible === false);

      if (visible.length > 0) {
          return;
      }

      if (hidden.length > 0) {
          await showWidget(hidden[0].widgetId);
      } else {
          await addWidget(definitionId);
      }
  }, [layouts, showWidget, addWidget]);

  const updateGlobalFilters = useCallback(
    async (filters: GlobalFilters) => {
      if (!layouts) return;

      setGlobalFilters(filters);
      setLayouts((prev) => (prev ? { ...prev, globalFilters: filters } : prev));

      try {
        if (Object.keys(filters).length === 0) {
          await DashboardApi.clearGlobalFilters(layouts.layoutKey);
        } else {
          await DashboardApi.saveGlobalFilters(layouts.layoutKey, filters);
        }
      } catch (error) {
        console.error("Failed to save global filters:", error);
        throw error;
      }
    },
    [layouts]
  );

  const refreshWidget = useCallback((widgetId: string) => {
      setRefreshTokens((prev) => ({
          ...prev,
          [widgetId]: (prev[widgetId] ?? 0) + 1,
      }));
  }, []);

  const refreshAll = useCallback(() => {
      if (!layouts?.widgets) return;
      setRefreshTokens((prev) => {
          const next = { ...prev };
          for (const w of layouts.widgets) {
              next[w.widgetId] = (next[w.widgetId] ?? 0) + 1;
          }
          return next;
      });
  }, [layouts?.widgets]);

  const updateLayout = useCallback(async (nextLayouts: BreakpointLayouts) => {
    if (!layouts) return;

    const updatedLayout: DashboardLayoutConfig = {
      ...layouts,
      layouts: { ...layouts.layouts, ...nextLayouts } as BreakpointLayouts,
    };

    setLayouts(updatedLayout);

    try {
      await DashboardApi.saveLayouts(layouts.layoutKey, nextLayouts);
    } catch (error) {
      console.error("Failed to save updated grid layout:", error);
      throw error;
    }
  }, [layouts]);

  const saveLayout = useCallback(async () => {
    if (!layouts) return;
    try {
      await DashboardApi.saveLayoutConfig(layouts);
    } catch (error) {
      console.error("Failed to save layout:", error);
      throw error;
    }
  }, [layouts]);

  const loadLayout = useCallback(async (newLayoutId: string) => {
    try {
      setIsLoading(true);
      const layout = await DashboardApi.loadLayoutConfig(newLayoutId);

      if (isLayoutEmpty(layout)) {
        const seededLayout = buildSeedLayout(newLayoutId, layout);
        await DashboardApi.saveLayoutConfig(seededLayout);
        setLayouts(seededLayout);
        setGlobalFilters(seededLayout.globalFilters || {});
        return;
      }

      if (layout && hasVisibleWidgets(layout) && !hasGrid(layout)) {
        const repairedLayout = repairMissingGrid(layout);
        await DashboardApi.saveLayoutConfig(repairedLayout);
        setLayouts(repairedLayout);
        setGlobalFilters(repairedLayout.globalFilters || {});
        return;
      }

      if (!layout) return;

      const reconciled = reconcileNewWidgets(layout);
      if (reconciled !== layout) {
          await DashboardApi.saveLayoutConfig(reconciled);
      }

      setLayouts(reconciled);
      setGlobalFilters(reconciled.globalFilters || {});
    } catch (error) {
      console.error(`Failed to load layout ${newLayoutId}:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value: WidgetContextValue = {
    layouts,
    globalFilters,
    isLoading,
    updateWidgetSettings,
    saveWidgetSettings,
    deleteWidget,
    addWidget,
    hideWidget,
    showWidget,
    addOrShowWidget,
    updateGlobalFilters,
    refreshWidget,
    refreshAll,
    updateLayout,
    saveLayout,
    loadLayout,
  };

  return (
    <WidgetContext.Provider value={value}>
      <WidgetRefreshProvider
        refreshTokens={refreshTokens}
      >
        {children}
      </WidgetRefreshProvider>
    </WidgetContext.Provider>
  );
};

export const useWidgetContext = (): WidgetContextValue => {
  const context = useContext(WidgetContext);
  if (!context) {
    throw new Error("useWidgetContext must be used within WidgetProvider");
  }
  return context;
};

interface RefreshContextValue {
    refreshTokens: Record<string, number>;
}

const RefreshContext = createContext<RefreshContextValue | undefined>(
  undefined
);

const WidgetRefreshProvider = ({
  children,
  refreshTokens,
}: {
  children: ReactNode;
  refreshTokens: Record<string, number>;
}) => {
  const value: RefreshContextValue = {
    refreshTokens,
  };

  return (
    <RefreshContext.Provider value={value}>
      {children}
    </RefreshContext.Provider>
  );
};

export const useRefreshContext = (): RefreshContextValue => {
  const context = useContext(RefreshContext);
  if (!context) {
    throw new Error("useRefreshContext must be used within WidgetProvider");
  }
  return context;
};
